'use client'

import { Avatar, Box, HStack, Image, Stack, Tag, Text } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { LuMapPin } from 'react-icons/lu'

import type {
  CircleWizardFormData,
  CommunityTypeValue,
} from '@/components/molecules/Form/Circles/CircleForm/newCircle/types'
import { useColorModeValue } from '@/components/ui/color-mode'

const previewFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`

const TYPE_I18N: Record<CommunityTypeValue, string> = {
  neighborhood: 'neighborhood',
  building: 'building',
  association: 'association',
  company: 'company',
  local_authority: 'localAuthority',
  third_place: 'thirdPlace',
  school: 'school',
  cooperative: 'cooperative',
  club: 'club',
  family: 'family',
  friends: 'friends',
  other: 'other',
}

export type CommunityCardPreviewProps = Pick<
  CircleWizardFormData,
  'name' | 'type' | 'customTypeLabel' | 'city' | 'logo' | 'cover'
>

/**
 * Live preview of the community being created in {@link NewCircleWizard}.
 * Standalone presentational card (no GraphQL fragment), fed by the wizard form values.
 */
export const CommunityCardPreview = ({
  name,
  type,
  customTypeLabel,
  city,
  logo,
  cover,
}: CommunityCardPreviewProps) => {
  const { t } = useTranslation()
  const cardBg = useColorModeValue('white', 'gray.800')
  const coverBg = useColorModeValue('blue.100', 'blue.900')
  const previewShadow = useColorModeValue(
    '0 22px 50px -14px rgba(26, 32, 44, 0.38), 0 8px 20px -8px rgba(26, 32, 44, 0.18)',
    '0 28px 56px -16px rgba(0, 0, 0, 0.65), 0 12px 28px -10px rgba(0, 0, 0, 0.45)'
  )

  const displayName =
    name.trim() || t('communities.new.preview.namePlaceholder')

  return (
    <Box w="full" maxW="md" mx="auto">
      <Box
        borderRadius="2xl"
        overflow="hidden"
        bg={cardBg}
        boxShadow={previewShadow}
        animation={`${previewFloat} 5s ease-in-out infinite`}
        willChange="transform"
      >
        <Box h="28" bg={coverBg} position="relative">
          {cover?.base64 && (
            <Image
              src={cover.base64}
              alt=""
              w="full"
              h="full"
              objectFit="cover"
            />
          )}
          <Box position="absolute" bottom="-8" left="6">
            <Avatar.Root size="xl" shape="rounded">
              {logo?.base64 ? (
                <Avatar.Image src={logo.base64} alt={displayName} />
              ) : (
                <Avatar.Fallback name={displayName} />
              )}
            </Avatar.Root>
          </Box>
        </Box>
        <Stack gap={2} px="6" pt="10" pb="6">
          <HStack justify="space-between" align="start">
            <Text fontWeight="bold" fontSize="lg" lineClamp={1}>
              {displayName}
            </Text>
            <Tag.Root size="sm" colorPalette="blue">
              <Tag.Label>
                {type === 'other' && customTypeLabel?.trim()
                  ? customTypeLabel
                  : t(`communities.new.type.${TYPE_I18N[type]}`)}
              </Tag.Label>
            </Tag.Root>
          </HStack>
          {city?.trim() && (
            <HStack color="fg.muted" fontSize="sm" gap={1}>
              <LuMapPin />
              <Text lineClamp={1}>{city}</Text>
            </HStack>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
